import { aiImages, state } from '../core/state.mjs';
import { CONFIG } from '../core/config.mjs';
import { detectFormats } from '../detection/detect-formats.mjs';
import { normalizeImageSrc, isPngImage } from '../utils/image-utils.mjs';
import { getCachedContainerWidth } from '../utils/cache.mjs';
import { lazyLoading } from './lazy-loading.mjs';

/**
 * Transforme images ai-src en images optimisées responsive + lazy loading
 */
export async function imageTransform() {
    if (!aiImages.length) return;

    const formats = await detectFormats();
    const baseUrl = window.location.protocol + '//' + window.location.host;

    aiImages.forEach(img => {
        const src = img.getAttribute('ai-src');
        if (!src) return;

        // Vérifier si l'image est dans une picture (marqueur PHP)
        const isInPicture = img.hasAttribute('ai-in-picture');

        const normalizedSrc = normalizeImageSrc(src);
        const isPng = isPngImage(normalizedSrc);

        // Détermine le meilleur format
        let bestFormat = '';

        if (isPng && CONFIG.formats.pngToWebP && formats.webp) {
            bestFormat = 'webp';
            if (CONFIG.debug) {
                console.log(`[AI] PNG forcé en WebP: ${normalizedSrc}`);
            }
        } else if (CONFIG.formats.preferredFormat === 'avif' && (!CONFIG.formats.autoDetect || formats.avif)) {
            bestFormat = 'avif';
        } else if (CONFIG.formats.preferredFormat === 'webp' && (!CONFIG.formats.autoDetect || formats.webp)) {
            bestFormat = 'webp';
        } else if (CONFIG.formats.preferredFormat === 'auto') {
            if (formats.avif && !(isPng && CONFIG.formats.pngToWebP)) {
                bestFormat = 'avif';
            } else if (formats.webp) {
                bestFormat = 'webp';
            }
        }

        if (CONFIG.debug) {
            console.log(`[AI] Format: ${bestFormat || 'original'}${isInPicture ? ' (dans picture)' : ''}`);
        }

        const container = img.parentElement || document.body;
        const containerWidth = getCachedContainerWidth(container); // Largeur du conteneur calculée
        const formatParam = bestFormat ? `&format=${bestFormat}` : '';

        // Génère srcset responsive avec différentes densités
        const srcset = CONFIG.responsive.densities.map(density =>
            `${normalizedSrc}${formatParam}&width=${Math.floor(containerWidth * density)}&org_if_sml=1 ${density}x`
        ).join(',');

        // Détermine sizes selon breakpoints
        let sizes;
        if (containerWidth <= CONFIG.responsive.breakpoints.small) {
            sizes = CONFIG.responsive.sizes.small;
        } else if (containerWidth <= CONFIG.responsive.breakpoints.medium) {
            sizes = CONFIG.responsive.sizes.medium;
        } else {
            sizes = CONFIG.responsive.sizes.large;
        }

        if (CONFIG.lazyLoading.enabled) {
            // Mode lazy loading
            img.setAttribute('data-srcset', srcset);
            img.setAttribute('data-src', `${normalizedSrc}${formatParam}&width=${containerWidth}&org_if_sml=1`);
            img.setAttribute('src', CONFIG.lazyLoading.placeholder);
            img.setAttribute('sizes', sizes);
            img.classList.add('lazy-image');
        } else {
            // Chargement immédiat avec taille calculée
            const actualSrc = `${normalizedSrc}${formatParam}&width=${containerWidth}&org_if_sml=1`;

            // Définir un placeholder temporaire pour éviter l'image cassée
            if (!img.hasAttribute('src')) {
                img.setAttribute('src', CONFIG.lazyLoading.placeholder);
            }

            const tempImg = new Image();

            tempImg.onload = function () {
                img.setAttribute('srcset', srcset);
                img.setAttribute('src', actualSrc);
                img.setAttribute('sizes', sizes);
                img.classList.add('ai-loaded');
            };

            tempImg.onerror = function () {
                img.setAttribute('srcset', srcset);
                img.setAttribute('src', actualSrc);
                img.setAttribute('sizes', sizes);
                img.classList.add('ai-loaded');
                console.warn(`[AI] Erreur: ${actualSrc}`);
            };

            tempImg.srcset = srcset;
            tempImg.src = actualSrc;
        }

        if (!img.hasAttribute('alt')) img.setAttribute('alt', '');

        // Supprimer les marqueurs de traitement
        img.removeAttribute('ai-src');
        if (isInPicture) {
            img.removeAttribute('ai-in-picture');
        }

        state.images.set(img, {
            src: normalizedSrc,
            width: containerWidth,
            format: bestFormat,
            initialWidth: containerWidth,
            useOriginal: false
        });
    });

    if (CONFIG.lazyLoading.enabled && aiImages.length) {
        lazyLoading();
    }
}
